import React from 'react';
import { PopupWrapperView } from '@src/features/order/pages/orders-page/components';
import { CompletedPopupProps } from './types';
import { ArcStack, ArcCellInfo, ArcBox, ArcText, ArcButton } from '@src/core/ui';
import { OrderMissionEntity, OrderStatusEnum, OrderStatusNames, OrderEntity } from '@src/features/order/entities';
import { arcDateWithTime, arcSpatium, arcSecToMin, arcVelocitas } from '@src/core/extensions';
import { OrderDetailsStatusColors } from '@src/core/constants/order-status-colors';
import OrdersMap from '@src/features/order/pages/orders-page/map';
import { ArcGoogleMap, setMapLocation } from '@src/core/controllers';
import { EMPTY_VALUE } from '@src/core/config';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setOrderFormData } from '@src/features/order/pages/orders-page/redux';
import { routesConfig } from '@src/core/routes';

const CompletedPopup: React.FC<CompletedPopupProps> = (props) => {
  const { order = {} as OrderMissionEntity } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getOrderInfo = () => {
    const orderData = order as Record<string, any>;

    if (orderData.orders && Array.isArray(orderData.orders) && orderData.orders.length > 0) {
      const firstOrder = orderData.orders[0];
      return {
        id: firstOrder?.id || "N/A",
        createDate: firstOrder?.createDate,
        status: firstOrder?.status,
        packageContent: firstOrder?.packageContent || "N/A",
        clientName: firstOrder?.clientName || "N/A",
        deliveryLocation: firstOrder?.deliveryLocation,
        drone: orderData?.drone?.name,
        totalFlightTime: firstOrder?.totalFlightTime || 0,
        totalDistance: firstOrder?.totalDistance || 0,
        flightAltitude: firstOrder?.flightAltitude || 0,
        deliveryAltitude: firstOrder?.deliveryAltitude || 0,
        cruiseSpeed: firstOrder?.drone?.speed || 0,
        latitude: firstOrder?.deliveryLocation?.latitude || 0,
        longtitude: firstOrder?.deliveryLocation?.longitude || 0,
      };
    }

    return {
      id: orderData?.id || "N/A",
      createDate: orderData?.createDate,
      status: orderData?.status,
      packageContent: orderData?.packageContent || "N/A",
      clientName: orderData?.clientName || "N/A",
      deliveryLocation: orderData?.deliveryLocation,
      drone: orderData?.drone?.name,
      totalFlightTime: orderData?.totalFlightTime || 0,
      totalDistance: orderData?.totalDistance || 0,
      flightAltitude: orderData?.flightAltitude || 0,
      deliveryAltitude: orderData?.deliveryAltitude || 0,
      cruiseSpeed: orderData?.drone?.speed || 0,
      latitude: orderData?.deliveryLocation?.latitude || 0,
      longtitude: orderData?.deliveryLocation?.longitude || 0,
    };
  };

  const orderInfo = getOrderInfo();

  return (
    <PopupWrapperView title={'MISSION DETAILS'}>
      <ArcStack direction='row' spacing={2} width='100%'>
        <ArcStack width='50%'>
          <ArcStack my={1} spacing={2}>
            <ArcStack direction="row" spacing={4}>
              <ArcStack spacing={2} flex={1}>
                <ArcCellInfo label="Date" textProps={{ fontWeight: 400 }}>
                  {arcDateWithTime(orderInfo.createDate?.toString() || "")}
                </ArcCellInfo>

                <ArcCellInfo label="Status" textProps={{ fontWeight: 400 }}>
                  <ArcStack direction="row" alignItems="center" spacing={1}>
                    <ArcBox
                      width={8}
                      height={8}
                      borderRadius="50%"
                      bgcolor={
                        OrderDetailsStatusColors[
                          orderInfo.status.toLowerCase() as OrderStatusEnum
                        ]
                      }
                    />
                    <ArcText color="white" fontSize={14} fontWeight={400}>
                      {OrderStatusNames[orderInfo.status.toLowerCase() as OrderStatusEnum]}
                    </ArcText>
                  </ArcStack>
                </ArcCellInfo>

                <ArcCellInfo label="Duration" textProps={{ fontWeight: 400 }}>
                  {arcSecToMin(orderInfo.totalFlightTime)}
                </ArcCellInfo>

                <ArcCellInfo label="Flight Altitude" textProps={{ fontWeight: 400 }}>
                  {arcSpatium(orderInfo.flightAltitude)}
                </ArcCellInfo>

                <ArcCellInfo label="Cruise Speed" textProps={{ fontWeight: 400 }}>
                  {arcVelocitas(orderInfo.cruiseSpeed)}
                </ArcCellInfo>

                <ArcCellInfo label="Package Content" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={{ fontWeight: 400 }}>
                  {orderInfo.packageContent}
                </ArcCellInfo>

                <ArcCellInfo label="Latitude" textProps={{ fontWeight: 400 }}>
                  {(Number(orderInfo.latitude).toFixed(4) || EMPTY_VALUE) + "° N"}
                </ArcCellInfo>
              </ArcStack>

              <ArcStack spacing={2} flex={1}>
                <ArcCellInfo label="Order" textProps={{ fontWeight: 400 }}>#{orderInfo.id}</ArcCellInfo>

                <ArcCellInfo label="Drone" textProps={{ fontWeight: 400 }}>
                  {orderInfo?.drone || "N/A"}
                </ArcCellInfo>

                <ArcCellInfo label="Distance" textProps={{ fontWeight: 400 }}>
                  {arcSpatium(orderInfo.totalDistance)}
                </ArcCellInfo>

                <ArcCellInfo label="Delivery Altitude" textProps={{ fontWeight: 400 }}>  
                  {arcSpatium(orderInfo.deliveryAltitude)}
                </ArcCellInfo>

                <ArcCellInfo label="Client Name" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={{ fontWeight: 400 }}>
                  {orderInfo.clientName}
                </ArcCellInfo>

                <ArcCellInfo label="Longtitude" textProps={{ fontWeight: 400 }}>
                  {(Number(orderInfo.longtitude).toFixed(4) || EMPTY_VALUE) + "° E"}
                </ArcCellInfo>
              </ArcStack>
            </ArcStack>

            <ArcCellInfo label="Destination" sx={{ maxWidth: "300px", marginBottom: 1 }} textProps={{ fontWeight: 400 }}>
              {orderInfo.deliveryLocation?.address || "N/A"}
            </ArcCellInfo>

            <ArcStack>
              <ArcButton.brand
                fullWidth 
                onClick={() => {
                  if (order) {
                    const orderData = order as Record<string, any>;
                    const orderToRepeat = Array.isArray(orderData.orders) ? orderData.orders[0] : orderData;
                    
                    dispatch(setOrderFormData(orderToRepeat as unknown as OrderEntity));
                    dispatch(setMapLocation({
                      latitude: Number(orderToRepeat.deliveryLocation?.latitude) || 0,
                      longitude: Number(orderToRepeat.deliveryLocation?.longitude) || 0,
                      address: orderToRepeat.deliveryLocation?.address || "",
                    }));
                    navigate(routesConfig.createOrder);
                  }
                }}
                sx={{ marginTop: 0.5}}> 
                Repeat 
              </ArcButton.brand> 
            </ArcStack>
          </ArcStack>
        </ArcStack>
        <ArcStack width='336px' borderRadius={1} sx={{ overflow: 'hidden' }}>
          <ArcGoogleMap hideToolbar hideMapControls>
            <OrdersMap />
          </ArcGoogleMap>
        </ArcStack>
      </ArcStack>
    </PopupWrapperView>
  );
};

export default CompletedPopup;

